/**
 * VoiceConfirm — Tela de confirmação dos dados extraídos por IA após gravação de voz.
 * Recebe os dados via router state e permite editar antes de salvar.
 */
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle2, X, Mic, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { useI18n } from "@/contexts/I18nContext";
import { isCategoryForType } from "@/i18n/categories";

interface VoiceData {
  transcription: string;
  amount: number | null;
  description: string;
  type: "income" | "expense" | "transfer";
  category: string;
}

export default function VoiceConfirm() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as VoiceData | null;
  const { t, ui, currencySymbol, categoryName } = useI18n();

  const [type, setType] = useState<"income" | "expense" | "transfer">(state?.type ?? "expense");
  const [amount, setAmount] = useState(state?.amount != null ? String(state.amount) : "");
  const [description, setDescription] = useState(state?.description ?? "");
  const [categoryId, setCategoryId] = useState<string>("");
  const [date] = useState(() => new Date());

  const { data: categories = [] } = trpc.categories.list.useQuery({});
  const utils = trpc.useUtils();

  const createMutation = trpc.transactions.create.useMutation({
    onSuccess: () => {
      utils.transactions.list.invalidate();
      utils.transactions.monthSummary.invalidate();
      toast.success(ui.transactionForm.created);
      navigate("/transactions");
    },
    onError: (err) => {
      toast.error(err.message || t.common.error);
    },
  });

  // Ao carregar categorias, tenta encontrar a categoria sugerida pela IA
  useEffect(() => {
    if (categories.length > 0 && state?.category && !categoryId) {
      const match = categories.find(
        (c) => c.name.toLowerCase() === state.category.toLowerCase() && isCategoryForType(c, type)
      );
      if (match) setCategoryId(String(match.id));
    }
  }, [categories, state?.category]);

  // Filtra categorias por tipo
  const expenseCategories = categories.filter((c) => isCategoryForType(c, "expense"));
  const incomeCategories = categories.filter((c) => isCategoryForType(c, "income"));
  const transferCategories = categories.filter((c) => isCategoryForType(c, "transfer"));
  const visibleCategories =
    type === "income" ? incomeCategories :
    type === "transfer" ? transferCategories :
    expenseCategories;

  function handleSave() {
    const parsedAmount = parseFloat(amount.replace(",", "."));
    if (!parsedAmount || parsedAmount <= 0) {
      toast.error(ui.transactionForm.invalidAmount);
      return;
    }
    createMutation.mutate({
      type,
      amount: parsedAmount,
      description: description || undefined,
      date,
      categoryId: categoryId ? parseInt(categoryId) : undefined,
      aiCategorized: true,
    });
  }

  if (!state) {
    // Sem dados, volta para a gravação
    navigate("/voice");
    return null;
  }

  const typeLabel = type === "income" ? t.transactions.income : type === "transfer" ? t.transactions.transfer : t.transactions.expense;
  const typeColor = type === "income" ? "text-emerald-600" : type === "transfer" ? "text-blue-600" : "text-red-500";
  const typeBg = type === "income" ? "bg-emerald-50 border-emerald-200" : type === "transfer" ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 pt-6 pb-4">
        <button
          onClick={() => navigate("/voice")}
          className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
          aria-label={t.voice.retry}
        >
          <Mic className="w-5 h-5 text-muted-foreground" />
        </button>
        <h1 className="font-serif text-xl font-semibold text-foreground">{t.voice.confirm}</h1>
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-muted flex items-center justify-center hover:bg-muted/80 transition-colors"
          aria-label={t.transactions.cancel}
        >
          <X className="w-5 h-5 text-muted-foreground" />
        </button>
      </div>

      {/* Transcrição */}
      {state.transcription && (
        <div className="mx-6 mb-4 px-4 py-3 bg-muted/40 rounded-xl border border-border">
          <p className="text-xs text-muted-foreground mb-1 font-medium uppercase tracking-wide">{t.transactions.description}</p>
          <p className="text-sm text-foreground italic">"{state.transcription}"</p>
        </div>
      )}

      {/* Tipo detectado */}
      <div className="mx-6 mb-6">
        <div className={cn("flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium", typeBg, typeColor)}>
          <CheckCircle2 className="w-4 h-4" />
          <span>{t.transactions.type}: <strong>{typeLabel}</strong></span>
        </div>
      </div>

      {/* Formulário de confirmação */}
      <div className="flex-1 px-6 space-y-5">
        {/* Tipo */}
        <div className="space-y-1.5">
          <Label>{t.transactions.type}</Label>
          <div className="grid grid-cols-3 gap-2">
            {(["expense", "income", "transfer"] as const).map((option) => (
              <button
                key={option}
                onClick={() => { setType(option); setCategoryId(""); }}
                className={cn(
                  "py-2 rounded-lg text-sm font-medium border transition-all",
                  type === option
                    ? option === "income"
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : option === "transfer"
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-red-500 text-white border-red-500"
                    : "bg-background text-muted-foreground border-border hover:bg-muted/50"
                )}
              >
                {option === "expense" ? t.transactions.expense : option === "income" ? t.transactions.income : t.transactions.transfer}
              </button>
            ))}
          </div>
        </div>

        {/* Valor */}
        <div className="space-y-1.5">
          <Label htmlFor="amount">{t.transactions.amount} ({currencySymbol})</Label>
          <Input
            id="amount"
            type="number"
            inputMode="decimal"
            placeholder={ui.transactionForm.amountPlaceholder}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="text-2xl font-bold h-14"
          />
        </div>

        {/* Descrição */}
        <div className="space-y-1.5">
          <Label htmlFor="desc">{t.transactions.description}</Label>
          <Input
            id="desc"
            placeholder={ui.transactionForm.descriptionPlaceholder}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        {/* Categoria */}
        <div className="space-y-1.5">
          <Label>{t.transactions.category}</Label>
          <Select value={categoryId} onValueChange={setCategoryId}>
            <SelectTrigger>
              <SelectValue placeholder={ui.transactionForm.selectCategory} />
            </SelectTrigger>
            <SelectContent>
              {visibleCategories.map((cat) => (
                <SelectItem key={cat.id} value={String(cat.id)}>
                  {categoryName(cat.name)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Botões de ação */}
      <div className="px-6 pb-8 pt-6 space-y-3">
        <Button
          className="w-full h-14 text-base font-semibold"
          onClick={handleSave}
          disabled={createMutation.isPending || !amount}
        >
          {createMutation.isPending ? t.common.loading : t.transactions.save}
        </Button>
        <Button
          variant="ghost"
          className="w-full"
          onClick={() => navigate("/voice")}
        >
          {t.voice.retry}
        </Button>
      </div>
    </div>
  );
}
